import { useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  computeBudgetTotals,
  getFiscalYearLabel,
  totalExternalCharges,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Money } from '@/components/ui'
import { BudgetCodeEditor, budgetCodeTitle } from './BudgetCodeEditor'
import { useCodesById } from './hooks'
import { TransactionDetail } from '@/features/transactions/TransactionDetail'
import { TransactionList } from '@/features/transactions/TransactionList'
import { useTransactionsForCode } from '@/features/transactions/hooks'
import { freshnessFor, useCsiuFreshness } from '@/features/csiu/freshness'
import { BalanceStrip } from './BalanceStrip'
import { useBudgetCode } from './hooks'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'
import { humanErrorMessage } from '@/lib/errorMessage'

/**
 * Budget code detail — workflow 7.5 step 4, §8.2a.
 *
 * Reachable from a dashboard card, from lookup, or from a bookmarked URL —
 * §8.2a also makes it the destination for threshold-alert emails, so the code
 * ID lives in the path rather than in component state.
 *
 * External codes get a different view: no beginning balance, no remaining, no
 * threshold warnings. There is no budget to track, only a running total of
 * what's been charged on someone else's behalf.
 */
export function BudgetCodeDetail() {
  const { codeId } = useParams<{ codeId: string }>()
  const navigate = useNavigate()
  const fiscalYear = useFiscalYear()

  const codeQuery = useBudgetCode(codeId, fiscalYear)
  const { data: code, error } = codeQuery
  const state = loadStateOf(codeQuery)
  const { data: transactions } = useTransactionsForCode(codeId, fiscalYear)
  const { data: importsByCode } = useCsiuFreshness()
  const [selectedTransactionId, setSelectedTransactionId] = useState<string>()
  const [editing, setEditing] = useState(false)

  const freshness = freshnessFor(importsByCode, codeId)

  // Resolved across years, not just the one on screen: a charge moved to
  // last year still points at the code document it was filed against.
  const codesById = useCodesById()

  if (selectedTransactionId !== undefined) {
    return (
      <TransactionDetail
        transactionId={selectedTransactionId}
        codesById={codesById}
        fiscalYear={fiscalYear}
        onClose={() => setSelectedTransactionId(undefined)}
      />
    )
  }

  // "Budget code not found" below is a claim about the database, so it waits
  // until a query has answered — offline it would otherwise read as deleted.
  if (state !== 'ready') {
    return <LoadingOrOffline state={state} what="This budget code" />
  }

  if (error != null) {
    return <ErrorNote>{humanErrorMessage(error, "Couldn't load this budget code.")}</ErrorNote>
  }

  if (code === undefined) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">Budget code not found</h1>
        <p className="text-slate-600">
          It may have been removed, or belong to a different fiscal year.
        </p>
        <Link to="/codes" className="inline-block underline underline-offset-4">
          All budget codes
        </Link>
      </div>
    )
  }

  // Editing replaces the whole view rather than sitting above it: a balance
  // strip and a transaction list over a half-edited beginning balance are two
  // versions of the same code on one screen.
  if (editing) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-semibold tracking-tight">
          Edit {budgetCodeTitle(code)}
        </h1>
        <Card>
          <BudgetCodeEditor
            code={code}
            fiscalYear={fiscalYear}
            onDone={() => setEditing(false)}
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="ghost" onClick={() => void navigate(-1)} className="-ml-4">
            ← Back
          </Button>
          <Button onClick={() => setEditing(true)}>Edit</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {budgetCodeTitle(code)}
          </h1>
          {code.external && <Badge tone="warn">External</Badge>}
          {code.fund === 'grant' && <Badge tone="ok">Grant</Badge>}
          {!code.active && <Badge>Deactivated</Badge>}
        </div>

        <p className="font-mono text-sm text-slate-500">{code.code}</p>

        <div className="flex flex-wrap items-center gap-2">
          {code.aliases.map((alias) => (
            <span
              key={alias}
              className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600"
            >
              {alias}
            </span>
          ))}
          {!code.external && (
            <span className="text-xs text-slate-400">
              Category: {code.category} ·
            </span>
          )}
          <span className="text-xs text-slate-400">
            {getFiscalYearLabel(fiscalYear)}
          </span>
        </div>
      </header>

      {code.external ? (
        <ExternalSummary transactions={transactions} ownerName={code.externalOwnerName} />
      ) : (
        <Card>
          <BalanceStrip
            totals={computeBudgetTotals(code.beginningBalance, transactions)}
            alertThresholdPercent={code.alertThresholdPercent}
          />
          {/* §7.5 step 5: how current the accounting side is for this code.
              Read from the import history rather than from the transactions —
              an import that matched everything creates no new rows, so the
              newest transaction here can be far older than the last time this
              code was actually reconciled. */}
          <p
            className={`mt-4 border-t border-slate-100 pt-3 text-xs ${
              freshness.stale ? 'text-amber-700' : 'text-slate-400'
            }`}
          >
            {freshness.daysSince === null ? (
              <>
                No CSIU import has covered this code yet.{' '}
                <Link to="/import" className="underline underline-offset-4">
                  Import a cycle
                </Link>
                .
              </>
            ) : freshness.daysSince === 0 ? (
              'CSIU data imported today.'
            ) : (
              `CSIU data last imported ${freshness.daysSince} day${
                freshness.daysSince === 1 ? '' : 's'
              } ago${freshness.stale ? ' — worth refreshing.' : '.'}`
            )}
          </p>
        </Card>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Transactions ({transactions.length})
        </h2>
        <TransactionList
          transactions={transactions}
          codesById={codesById}
          showCode={false}
          onSelect={(t) => setSelectedTransactionId(t.id)}
          emptyTitle="No transactions on this code yet"
          emptyBody={
            <p>
              Add one from the{' '}
              <Link to="/transactions" className="underline underline-offset-4">
                Transactions
              </Link>{' '}
              screen.
            </p>
          }
        />
      </section>
    </div>
  )
}

/**
 * External codes have no balance to track (§8.2a), so the balance strip is
 * replaced by a total-charges-to-date summary.
 */
function ExternalSummary({
  transactions,
  ownerName,
}: {
  transactions: readonly Transaction[]
  ownerName: string | null
}) {
  const total = totalExternalCharges(transactions)
  return (
    <Card className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Total charged{ownerName !== null ? ` for ${ownerName}` : ''}
      </p>
      <Money value={total} className="text-2xl font-semibold" />
      <p className="text-sm text-slate-600">
        This is someone else's budget. These charges appear on your reconciliation
        packet but count against none of your balances.
      </p>
    </Card>
  )
}
